import React, { useState, useEffect } from 'react';
import { 
  Send, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Play,
  Loader2,
  Calendar,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as api from '@/lib/api';

const StatusIcon = ({ status }) => {
  const icons = {
    queued: <Clock className="w-4 h-4 text-yellow-500" />,
    scheduled: <Calendar className="w-4 h-4 text-blue-500" />,
    publishing: <Loader2 className="w-4 h-4 text-secondary animate-spin" />,
    published: <CheckCircle className="w-4 h-4 text-green-500" />,
    failed: <XCircle className="w-4 h-4 text-destructive" />
  };
  return icons[status] || <Clock className="w-4 h-4" />;
};

const PlatformBadge = ({ platform }) => {
  const styles = {
    youtube: 'bg-red-500/20 text-red-400 border-red-500/30',
    tiktok: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    instagram: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
  };

  const labels = {
    youtube: 'YouTube Shorts',
    tiktok: 'TikTok',
    instagram: 'Instagram Reels'
  };

  return (
    <Badge variant="outline" className={styles[platform]}>
      {labels[platform] || platform}
    </Badge>
  );
};

const PublishJobCard = ({ job, onPublish, onCancel, publishing }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-1">
              <StatusIcon status={job.status} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <PlatformBadge platform={job.platform} />
                <Badge variant="outline" className="capitalize">
                  {job.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Clip ID: {job.clip_id?.slice(0, 8)}...
              </p>
              {job.scheduled_at && (
                <p className="text-xs text-muted-foreground">
                  Scheduled: {formatDate(job.scheduled_at)}
                </p>
              )}
              {job.published_at && (
                <p className="text-xs text-green-500">
                  Published: {formatDate(job.published_at)}
                </p>
              )}
              {job.error_message && (
                <p className="text-xs text-destructive">
                  Error: {job.error_message}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            {(job.status === 'queued' || job.status === 'scheduled') && (
              <>
                <Button 
                  size="sm" 
                  onClick={() => onPublish(job.id)}
                  disabled={publishing === job.id}
                  data-testid={`publish-job-${job.id}`}
                >
                  {publishing === job.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-1" />
                      Publish Now
                    </>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => onCancel(job.id)}
                  data-testid={`cancel-job-${job.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Publishing() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await api.getPublishingQueue();
      setJobs(response.data.jobs || []);
    } catch (error) {
      toast.error('Failed to load publishing queue');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (jobId) => {
    setPublishing(jobId);
    try {
      await api.executePublish(jobId);
      toast.success('Publishing started!');
      setTimeout(fetchJobs, 2500);
    } catch (error) {
      toast.error('Failed to start publishing');
    } finally {
      setPublishing(null);
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await api.cancelPublishJob(jobId);
      setJobs(jobs.filter(j => j.id !== jobId));
      toast.success('Job cancelled');
    } catch (error) {
      toast.error('Failed to cancel job');
    }
  };

  const filterJobs = (status) => {
    if (status === 'all') return jobs;
    return jobs.filter(j => j.status === status);
  };

  const stats = {
    queued: jobs.filter(j => j.status === 'queued').length,
    scheduled: jobs.filter(j => j.status === 'scheduled').length,
    publishing: jobs.filter(j => j.status === 'publishing').length,
    published: jobs.filter(j => j.status === 'published').length,
    failed: jobs.filter(j => j.status === 'failed').length
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="publishing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Publishing Queue</h1>
          <p className="text-muted-foreground">Schedule and publish clips to social platforms</p>
        </div>
        <Button onClick={fetchJobs} variant="outline" data-testid="refresh-queue-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Queued', value: stats.queued, color: 'yellow-500' },
          { label: 'Scheduled', value: stats.scheduled, color: 'blue-500' },
          { label: 'Publishing', value: stats.publishing, color: 'secondary' },
          { label: 'Published', value: stats.published, color: 'green-500' },
          { label: 'Failed', value: stats.failed, color: 'destructive' }
        ].map((stat) => (
          <Card key={stat.label} className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50 border border-border">
          <TabsTrigger value="all">All ({jobs.length})</TabsTrigger>
          <TabsTrigger value="queued">Queued ({stats.queued})</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled ({stats.scheduled})</TabsTrigger>
          <TabsTrigger value="published">Published ({stats.published})</TabsTrigger>
        </TabsList>

        {['all', 'queued', 'scheduled', 'published', 'failed'].map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <Skeleton key={`skeleton-publish-${i}`} className="h-24 w-full" />
                ))}
              </div>
            ) : filterJobs(tab).length > 0 ? (
              <div className="space-y-4">
                {filterJobs(tab).map((job) => (
                  <PublishJobCard
                    key={job.id}
                    job={job}
                    onPublish={handlePublish}
                    onCancel={handleCancel}
                    publishing={publishing}
                  />
                ))}
              </div>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <Send className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">No jobs in queue</p>
                  <p className="text-sm text-muted-foreground">
                    Schedule clips from the Clips Library to publish
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Platform Connections */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Platform Connections</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: 'YouTube', icon: '▶️', connected: false },
              { name: 'TikTok', icon: '🎵', connected: false },
              { name: 'Instagram', icon: '📸', connected: false }
            ].map((platform) => (
              <div 
                key={platform.name}
                className="p-4 rounded-lg bg-muted/30 border border-border flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{platform.icon}</span>
                  <div>
                    <p className="font-medium">{platform.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {platform.connected ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" data-testid={`connect-${platform.name.toLowerCase()}-btn`}>
                  {platform.connected ? 'Disconnect' : 'Connect'}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-center">
            Connect platforms in Settings to enable automatic publishing
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
